// Chain-authoring + eligibility components (ADR-0040 phase 3 follow-up).
//
// Design-iteration build: these are real, shippable component functions —
// pure DOM, no I/O — but deliberately locale-AGNOSTIC (labels come in as
// data) so they can be iterated in the harness before being wired into the
// admin view with t()-resolved strings and real API calls. See
// docs/decisions/0035-component-harness.md for the workflow.

import '@material/web/select/outlined-select.js';
import '@material/web/select/select-option.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/text-button.js';
import '@material/web/iconbutton/icon-button.js';

import { ce, el, clear, avatarEl, type ValueEl } from '../dom.js';
import { field } from '../components/field.js';
import { icons } from '../icons.js';

/** An `md-outlined-select` built from [value, label] pairs. */
function mdSelect(
  attrs: Record<string, string>,
  opts: [string, string][],
  current = '',
): ValueEl {
  return ce(
    'md-outlined-select',
    attrs,
    ...opts.map(([value, label]) =>
      ce('md-select-option', { value, ...(value === current ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, label)),
    ),
  ) as ValueEl;
}

/** A 20px disc holding the group glyph — mirrors a member's avatar disc so
 *  group and person tokens look identical apart from the icon. */
function groupDisc(): HTMLElement {
  const node = el('span', { class: 'avatar elig-group-disc', 'aria-hidden': 'true' }, icons.group());
  node.style.width = '20px';
  node.style.height = '20px';
  return node;
}

export interface EligibilityRefsData {
  memberIds: number[];
  tagIds: number[];
}

/** A person or group the picker can target. */
export interface EligibilityOption {
  kind: 'member' | 'group';
  id: number;
  name: string;
  color?: string; // members only, for the avatar disc
}

export interface EligibilityPickerLabels {
  everyone: string;
  typePlaceholder: string; // input placeholder, e.g. "Type a name — kids, Ana…"
  groupTag: string; // muted suffix in suggestions, e.g. "group"
  noMatches: string;
  remove: string; // aria-label prefix, e.g. "Remove"
}

/**
 * Type-to-add targeting: tokens for the chosen members/groups sit inside a
 * text field; typing filters a suggestion menu (no "@", no forced dropdown —
 * you just type "kids" or "Ana"). Empty selection reads as "Everyone".
 * Emits the resolved eligibilityRefs on every change.
 */
export function eligibilityPicker(opts: {
  options: EligibilityOption[];
  selected: EligibilityRefsData;
  labels: EligibilityPickerLabels;
  onChange?: (refs: EligibilityRefsData) => void;
}): HTMLElement {
  const state: EligibilityRefsData = {
    memberIds: [...opts.selected.memberIds],
    tagIds: [...opts.selected.tagIds],
  };

  const field = el('div', { class: 'elig-field' });
  const input = el('input', { class: 'elig-input', type: 'text', placeholder: opts.labels.typePlaceholder });
  const menu = el('div', { class: 'elig-menu' });
  menu.style.display = 'none';
  const wrap = el('div', { class: 'elig-picker' }, field, menu);

  const isSelected = (o: EligibilityOption): boolean =>
    o.kind === 'member' ? state.memberIds.includes(o.id) : state.tagIds.includes(o.id);

  const emit = (): void =>
    opts.onChange?.({ memberIds: [...state.memberIds], tagIds: [...state.tagIds] });

  function add(o: EligibilityOption): void {
    if (o.kind === 'member') state.memberIds.push(o.id);
    else state.tagIds.push(o.id);
    input.value = '';
    emit();
    renderField();
    renderMenu();
    input.focus();
  }

  function remove(o: EligibilityOption): void {
    if (o.kind === 'member') state.memberIds = state.memberIds.filter((x) => x !== o.id);
    else state.tagIds = state.tagIds.filter((x) => x !== o.id);
    emit();
    renderField();
  }

  function token(o: EligibilityOption): HTMLElement {
    return el(
      'span',
      { class: `elig-chip ${o.kind === 'group' ? 'is-group' : 'is-member'}` },
      o.kind === 'member' ? avatarEl(o.name, '', o.color ?? 'var(--muted)', 20) : groupDisc(),
      el('span', { class: 'elig-name' }, o.name),
      el(
        'button',
        {
          type: 'button',
          class: 'elig-x',
          'aria-label': `${opts.labels.remove} ${o.name}`,
          onclick: () => remove(o),
        },
        '×',
      ),
    );
  }

  function matches(): EligibilityOption[] {
    const q = input.value.trim().toLowerCase();
    return opts.options
      .filter((o) => !isSelected(o))
      .filter((o) => q === '' || o.name.toLowerCase().includes(q));
  }

  function renderField(): void {
    clear(field);
    const selected = opts.options.filter(isSelected);
    if (selected.length === 0 && input.value === '') {
      field.append(el('span', { class: 'elig-everyone' }, opts.labels.everyone));
    }
    for (const o of selected) field.append(token(o));
    field.append(input);
  }

  function renderMenu(): void {
    clear(menu);
    if (document.activeElement !== input) {
      menu.style.display = 'none';
      return;
    }
    const found = matches();
    if (found.length === 0) {
      menu.style.display = '';
      menu.append(el('div', { class: 'elig-menu-empty muted' }, opts.labels.noMatches));
      return;
    }
    menu.style.display = '';
    for (const o of found) {
      menu.append(
        el(
          'button',
          { type: 'button', class: 'elig-menu-item', onmousedown: (ev: Event) => { ev.preventDefault(); add(o); } },
          o.kind === 'member' ? avatarEl(o.name, '', o.color ?? 'var(--muted)', 20) : groupDisc(),
          el('span', { class: 'grow' }, o.name),
          o.kind === 'group' ? el('span', { class: 'muted elig-kind' }, opts.labels.groupTag) : null,
        ),
      );
    }
  }

  input.addEventListener('input', () => { renderField(); renderMenu(); });
  input.addEventListener('focus', renderMenu);
  input.addEventListener('blur', () => setTimeout(() => { menu.style.display = 'none'; renderField(); }, 120));
  input.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent;
    if (e.key === 'Enter') {
      const found = matches();
      if (found.length > 0) { e.preventDefault(); add(found[0]!); }
    } else if (e.key === 'Backspace' && input.value === '') {
      const selected = opts.options.filter(isSelected);
      if (selected.length > 0) remove(selected[selected.length - 1]!);
    }
  });
  field.addEventListener('mousedown', (ev) => {
    if (ev.target === field) { ev.preventDefault(); input.focus(); }
  });

  renderField();
  return wrap;
}

// ---------------------------------------------------------------------------

export interface TaskTemplateOption {
  id: number;
  title: string;
  baseMinutes: number;
}

export interface TaskComposerLabels {
  nameLabel: string; // floating label on the name field
  placeholder: string; // hint shown only while the name field has focus
  worth: string; // floating label on the always-visible value input
  minSuffix: string;
  add: string;
  more: string;
  less: string;
  fromTemplate: string; // prefix, e.g. "Create task from template"
  whoCanDo: string;
  visibility: string;
  visEveryone: string;
  visAssignee: string;
  visAdmins: string;
  followupHeading: string;
  followupAfter: string;
  followupTemplatePlaceholder: string;
  followupAdd: string;
  followupRemove: string;
  addTask: string;
  saveAsTemplate: string;
  templateHint: string;
  elig: EligibilityPickerLabels;
}

/** A time-based chain: spawn template `templateId`, `delayMinutes` after this
 *  task completes (0 = immediately). */
export interface ComposedFollowup {
  templateId: number;
  delayMinutes: number;
}

export interface ComposedTask {
  title: string;
  baseMinutes: number;
  eligibilityRefs: EligibilityRefsData;
  visibility: 'everyone' | 'assignee' | 'admins';
  followups: ComposedFollowup[];
}

/**
 * Add-task composer: everyone starts with just a name field + Add + More.
 * Typing offers matching saved templates ("create task from template", so
 * templates work for unscheduled one-offs too). "More" reveals the full task
 * fields and a second action, "Save as template". Prototype: locale-agnostic
 * labels, callbacks instead of API calls.
 */
export function taskComposer(opts: {
  templates: TaskTemplateOption[];
  eligibilityOptions: EligibilityOption[];
  labels: TaskComposerLabels;
  onAdd?: (task: ComposedTask) => void;
  onSaveTemplate?: (task: ComposedTask) => void;
  onCreateFromTemplate?: (templateId: number) => void;
  /**
   * "More options" opens the full builder (UI-16), carrying the current
   * name/worth. When set, the composer's own inline more-panel and the
   * "Advanced…" link are suppressed — one escalation, not three.
   */
  onMore?: (task: ComposedTask) => void;
  /** Opens the power-options modal (recurrence, due date, labels). */
  onAdvanced?: () => void;
  advancedLabel?: string;
  // Optional integration seams for the real view (form semantics + stable ids).
  nameInputId?: string;
  worthInputId?: string;
  asForm?: boolean;
}): HTMLElement {
  const L = opts.labels;
  let refs: EligibilityRefsData = { memberIds: [], tagIds: [] };
  let expanded = false;
  let worthTouched = false;
  const followups: ComposedFollowup[] = [];

  // Both controls use the shared field primitive: a real <label for> that
  // floats out of the way on focus or value (UI-09, ADR-0046). The old
  // placeholder-only inputs had no accessible name and lost their only
  // description the moment someone typed.
  const nameField = field({
    id: opts.nameInputId ?? 'composer-name',
    label: L.nameLabel,
    placeholder: L.placeholder,
  });
  const input = nameField.input;
  input.classList.add('composer-input');
  const menu = el('div', { class: 'composer-menu' });
  menu.style.display = 'none';
  const inputWrap = el('div', { class: 'composer-input-wrap' }, nameField.root, menu);

  // A task must have a name AND a value — worth lives in the always-visible
  // row, everything else is optional under "More…".
  const worthField = field({
    id: opts.worthInputId ?? 'composer-worth',
    label: L.worth,
    type: 'number',
    value: '5',
    suffix: L.minSuffix,
    attrs: { min: '1' },
  });
  const worthInput = worthField.input;
  worthField.root.classList.add('composer-worth-field');
  worthInput.addEventListener('input', () => { worthTouched = true; });

  // Visibility is tracked in a plain variable, not read off the control at
  // submit time. A native <select> reports its first option even while
  // detached; an `md-outlined-select` reports '' until Lit has rendered it —
  // and in the UI-16 path (onMore) this panel is never mounted at all, so
  // reading `.value` there would post an empty visibility and 400.
  let visibility: ComposedTask['visibility'] = 'everyone';
  const visSel = mdSelect({ 'aria-label': L.visibility }, [
    ['everyone', L.visEveryone],
    ['assignee', L.visAssignee],
    ['admins', L.visAdmins],
  ], 'everyone');
  visSel.addEventListener('change', () => {
    if (visSel.value) visibility = visSel.value as ComposedTask['visibility'];
  });
  const pickerHost = el('div', {}, eligibilityPicker({
    options: opts.eligibilityOptions,
    selected: refs,
    labels: L.elig,
    onChange: (next) => { refs = next; },
  }));

  // A plain labelled row inside the More panel. Named to avoid shadowing the
  // imported `field()` primitive, which is what the name/worth controls use.
  const labelledRow = (labelText: string, ...controls: (Node | null)[]): HTMLElement =>
    el('div', { class: 'composer-field' }, el('label', {}, labelText), el('div', { class: 'row' }, ...controls));

  // Follow-up chain rows: "spawn <template> after <N> min" (0 = instant).
  const followupList = el('div', { class: 'composer-fu-list' });
  function renderFollowups(): void {
    clear(followupList);
    followups.forEach((f, idx) => {
      const sel = mdSelect(
        { 'aria-label': L.followupTemplatePlaceholder, class: 'composer-fu-select' },
        [['', L.followupTemplatePlaceholder], ...opts.templates.map((tpl): [string, string] => [String(tpl.id), tpl.title])],
        f.templateId ? String(f.templateId) : '',
      );
      sel.addEventListener('change', () => { f.templateId = Number(sel.value) || 0; });
      const delay = ce('md-outlined-text-field', {
        type: 'number', min: '0', value: String(f.delayMinutes),
        'aria-label': L.followupAfter, class: 'composer-fu-delay',
      }) as ValueEl;
      delay.addEventListener('input', () => { f.delayMinutes = Number(delay.value) || 0; });
      const removeBtn = ce('md-icon-button', { type: 'button', 'aria-label': L.followupRemove }, icons.close());
      removeBtn.addEventListener('click', () => { followups.splice(idx, 1); renderFollowups(); });
      followupList.append(
        el('div', { class: 'composer-fu' },
          // Row 1: which template to spawn.
          el('div', { class: 'composer-fu-r1' }, sel),
          // Row 2: when to spawn it (0 = instantly) + remove.
          el('div', { class: 'composer-fu-r2' },
            el('span', { class: 'muted' }, L.followupAfter),
            delay,
            el('span', { class: 'muted' }, L.minSuffix),
            el('span', { class: 'grow' }),
            removeBtn,
          ),
        ),
      );
    });
    const addFollowup = ce('md-text-button', { type: 'button', class: 'composer-fu-add' }, L.followupAdd);
    addFollowup.addEventListener('click', () => { followups.push({ templateId: 0, delayMinutes: 0 }); renderFollowups(); });
    followupList.append(addFollowup);
  }
  renderFollowups();

  function compose(): ComposedTask {
    return {
      title: input.value.trim(),
      baseMinutes: Number(worthInput.value) || 1,
      eligibilityRefs: refs,
      visibility,
      followups: followups.filter((f) => f.templateId > 0),
    };
  }

  // Material buttons default to type="submit" — inside the composer's <form>
  // that means every unmarked button silently submits it (and created a task
  // on every "More options" tap). Non-submit buttons say so explicitly.
  const moreBtn = ce('md-text-button', { type: 'button', class: 'composer-more-btn' }, opts.onMore ? L.more : `${L.more}…`);

  // The route to everything else (recurrence, due date, labels, timed
  // show/hide). It sits at the BOTTOM OF THIS PANEL, not on the action row:
  // "More…" and "Advanced…" side by side were two identical-looking
  // disclosures meaning different things, and nobody could predict which
  // held what (ADR-0047). One escalation per surface. UI-16 dissolves the
  // distinction entirely by folding both into the full builder.
  let advancedBtn: HTMLElement | null = null;
  if (opts.onAdvanced) {
    advancedBtn = ce('md-text-button', { type: 'button', class: 'composer-advanced-link', id: 'advanced-open' }, opts.advancedLabel ?? 'Advanced…');
    advancedBtn.addEventListener('click', () => opts.onAdvanced?.());
  }

  const panelAdd = ce('md-filled-button', { type: 'button' }, L.addTask);
  panelAdd.addEventListener('click', () => opts.onAdd?.(compose()));
  const panelSaveTemplate = ce('md-outlined-button', { type: 'button' }, L.saveAsTemplate);
  panelSaveTemplate.addEventListener('click', () => opts.onSaveTemplate?.(compose()));

  const morePanel = el(
    'div',
    { class: 'composer-more' },
    el('div', { class: 'composer-field' }, el('label', {}, L.whoCanDo), pickerHost),
    labelledRow(L.visibility, visSel),
    el('div', { class: 'composer-field' }, el('label', {}, L.followupHeading), followupList),
    el(
      'div',
      { class: 'row', style: 'margin-top:12px' },
      panelAdd,
      panelSaveTemplate,
      el('span', { class: 'muted composer-tpl-hint' }, L.templateHint),
    ),
    advancedBtn ? el('div', { class: 'composer-advanced-row' }, advancedBtn) : null,
  );
  morePanel.style.display = 'none';

  if (opts.onMore) {
    // UI-16: escalate to the full builder instead of toggling an inline panel.
    moreBtn.addEventListener('click', () => opts.onMore?.(compose()));
  } else {
    moreBtn.addEventListener('click', () => {
      expanded = !expanded;
      morePanel.style.display = expanded ? '' : 'none';
      moreBtn.textContent = expanded ? `${L.less}…` : `${L.more}…`;
    });
  }

  function renderMenu(): void {
    clear(menu);
    const q = input.value.trim().toLowerCase();
    if (document.activeElement !== input || q === '') {
      menu.style.display = 'none';
      return;
    }
    const found = opts.templates.filter((tpl) => tpl.title.toLowerCase().includes(q)).slice(0, 6);
    if (found.length === 0) {
      menu.style.display = 'none';
      return;
    }
    menu.style.display = '';
    for (const tpl of found) {
      menu.append(
        el(
          'button',
          { type: 'button', class: 'composer-menu-item', onmousedown: (ev: Event) => { ev.preventDefault(); opts.onCreateFromTemplate?.(tpl.id); input.value = ''; renderMenu(); } },
          el('span', { class: 'composer-menu-kicker muted' }, L.fromTemplate),
          el('span', { class: 'composer-menu-title' }, tpl.title),
          el('span', { class: 'muted composer-menu-min' }, `${tpl.baseMinutes} ${L.minSuffix}`),
        ),
      );
    }
  }

  // If the name exactly matches a saved template, prefill its worth (until
  // the user edits worth themselves) — the template is the source of truth
  // for "what this task is usually worth".
  function maybePrefillWorth(): void {
    if (worthTouched) return;
    const match = opts.templates.find((tpl) => tpl.title.trim().toLowerCase() === input.value.trim().toLowerCase());
    if (match) worthInput.value = String(match.baseMinutes);
  }

  input.addEventListener('input', () => { renderMenu(); maybePrefillWorth(); });
  input.addEventListener('focus', renderMenu);
  input.addEventListener('change', maybePrefillWorth);
  input.addEventListener('blur', () => { maybePrefillWorth(); setTimeout(() => { menu.style.display = 'none'; }, 120); });
  input.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Enter' && menu.style.display === 'none') opts.onAdd?.(compose());
  });

  // Material buttons are form-associated, so `type="submit"` still submits the
  // wrapping <form> — the form path stays real, not a click handler pretending.
  const addBtn = ce('md-filled-button', { class: 'composer-add-btn', type: opts.asForm ? 'submit' : 'button' }, L.add);
  if (!opts.asForm) addBtn.addEventListener('click', () => opts.onAdd?.(compose()));

  // Two lines: the task name on top, worth + actions on the second row.
  const nameRow = el('div', { class: 'composer-row composer-name-row' }, inputWrap);
  const actionRow = el(
    'div',
    { class: 'composer-row composer-action-row' },
    worthField.root,
    el('span', { class: 'grow' }),
    addBtn,
    moreBtn,
  );

  // No `.card` on the root: the CALLER owns the container (DESIGN-GUIDE
  // "Composition"). This component used to return a card and be rendered
  // inside the view's own card, inside the page — three borders around one
  // form, which is what the add-task area looked like on a phone.
  // When escalation goes to the full builder (onMore), the inline more-panel
  // is not part of the surface at all (UI-16).
  const inlinePanel = opts.onMore ? null : morePanel;
  if (opts.asForm) {
    return el(
      'form',
      { class: 'composer', onsubmit: (ev: Event) => { ev.preventDefault(); opts.onAdd?.(compose()); } },
      nameRow,
      actionRow,
      inlinePanel,
    );
  }
  return el('div', { class: 'composer' }, nameRow, actionRow, inlinePanel);
}
